import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import Aux from "../../../../hoc/Auxiliary";
import formatDate from "../../../Utils/Date/formatDate";
import TableContainer from "../Transactions/Table/TableContainer";
import DateFormated from "../../../Utils/Date/Date";
import DateFilters, {DEFAULT_DATE,TODAY} from "../Tables/DateFilters";
import Spinner from "../../../UI/Spinner";
import Export from "../../../Utils/XLS/Export";
import Get from "../../../Utils/API/Get";
import axios from "../../../../axios-defaults";
import Currency from "../../../Utils/Number/Currency";
import InvoiceReview from "../../../Documentations/InvoiceReview/InvoiceReview";
import {fireGTMDownloadFileEvent} from "../../../Utils/GTM/GTMHelper";

const getInvoiceLines = async ({ startDate, endDate, subAccountCode, pageNumber, pageSize }) => {
    let queryJson = {};

    if (pageNumber && pageSize) {
        queryJson = {
            subAccountCode,
            startDate,
            endDate,
            pageNumber,
            pageSize,
        };
    } else {
        queryJson = {
            subAccountCode,
            startDate,
            endDate,
        };
    }

    try {
        const transactions = await Get({
            endpoint: "/subAccounts/invoiceLines",
            queryJson,
        });
        if (!transactions) return false;
        return transactions;
    } catch (e) {
        console.error(e.message);
        return false;
    }
};

const filterMinStartDate = (invoices) => {
    return new Date(Math.min(...invoices.map(e => new Date(e.date))));
}

const invoices = (props) => {
    const { startDate, endDate, pageNumber, pageSize, currentPeriod, subAccountCode, subAccountType } = props;
    const { t, i18n } = useTranslation();

    const [itemsRaw, setItemsRaw] = useState([]);
    const [items, setItems] = useState([]);
    const [isSelectAll, setIsSelectAll] = useState(false);
    const [loading, setLoading] = useState(false);
    const [checked, setChecked] = useState([]);
	const [invoiceReview, setInvoiceReview] = useState({ open: false, data: {}});

	const getInvoices = async () => {
        let params = '';
        if (pageNumber && pageSize) {
            params = `subAccountCode=${subAccountCode}&pageNumber=${pageNumber}&pageSize=${pageSize}`
        } else {
            if (startDate === null || endDate === null) {
                return;
            } else {
                const currentStartDate = (startDate)?startDate:DEFAULT_DATE;
                const currentEndDate = (endDate)?endDate:formatDate(TODAY);
                params = `subAccountCode=${subAccountCode}&currentPeriod=${currentPeriod}&startDate=${currentStartDate}&endDate=${currentEndDate}`;
            }
        }
        await axios.get(`/subAccounts/invoiceHeaders?${params}`)
            .then(response => {
                if(response.data.succeeded) {
                    setItemsRaw(response.data.data);
                    mapInvoices(response.data.data);
                }
            }).catch(error => {
                console.log('[Account.js] : Invoices ajax error in getInvoices()',error.message)
            });
    }

    const mapInvoices = (invoices) => {
        let itemsArr = [];
        invoices.forEach((item) => {
			let address = item.subAccountAddress
				? `${item.subAccountAddress.addressLine1}${
					item.subAccountAddress.addressLine2
						? `, ${item.subAccountAddress.addressLine2}`
						: ""
				}, ${item.subAccountAddress.city}`
				: '';

            itemsArr.push({
                checkBox: item.invoiceNumber,
                account: {
                    name: item.subAccountName,
                    code: item.subAccountCode,
                    address: address,
                },
                invoiceNumber: item.invoiceNumber,
                amount: item.netAmount,
                dueDate: item.dueDate,
                date: item.transactionDateTime,
                balance: item.balanceAmount,
                status:
                    i18n.language === "fr"
                        ? item.transactionStatus.descriptionFr
                        : item.transactionStatus.descriptionEn,
                po: item.purchaseOrderNumber,
                documentPath: item.documentPath
            });
        });
        setItems(itemsArr);
    }

	const onClickInvoiceReviewHandler = (invoiceData) => {
		setInvoiceReview({ open: true, data: itemsRaw[invoiceData]});
	}

	const hideInvoiceReviewHandler = () => {
		setInvoiceReview({ open: false, data: []});
	}

    useEffect(async () => {
        setLoading(true);
        await getInvoices();
        setLoading(false);
    }, [startDate, endDate, pageNumber, pageSize]);

    useEffect(
        () =>
            isSelectAll
                ? setChecked(
                      itemsRaw.map((item) => item.invoiceNumber.toString())
                  )
                : setChecked([]),
        [isSelectAll]
    );

    const handleCheck = useCallback(
        (isChecked, value) => {
            if (isChecked && !checked.includes(value.toString())) {
                setChecked((checked) => [
                    ...checked.filter((thisChecked) => thisChecked !== value),
                    value,
                ]);
            } else {
                setChecked((checked) =>
                    checked.filter((thisChecked) => thisChecked !== value)
                );
            }
        },
        [checked]
    );

    const doXLSExport = useCallback((fileType) => {
        const checkedInvoices = itemsRaw
            .filter((item) => checked.includes(item.invoiceNumber.toString()))
            .map((item) => ({
                [t("export.invoices.subAccountCode")]: item.subAccountCode,
                [t("export.invoices.transactionDateTime")]:
                    new Date(item.transactionDateTime.split('T')[0]),
                [t("export.invoices.invoiceNumber")]: item.invoiceNumber,
                [t("export.invoices.dueDate")]: new Date(item.dueDate.split('T')[0]),
                [t("export.invoices.amount")]: item.grossAmount.value,
                [t("export.invoices.netAmount")]: item.netAmount.value,
                [t("export.invoices.poNumber")]: item.purchaseOrderNumber,
            }));
        Export(checkedInvoices, "harnois_export", [
            t("export.invoices.netAmount"),
            t("export.invoices.amount"),
        ], fileType);
		fireGTMDownloadFileEvent('facture',fileType+' - Sommaire');
    }, [checked, props.items]);

    const doXLSDetailExport = useCallback(async (fileType) => {
        setLoading(true);
        const transactions = await getInvoiceLines({
            subAccountCode,
            startDate: (pageNumber && pageSize) ? formatDate(filterMinStartDate(items)) : `${startDate.split("T")[0]}T00:00:00.000Z`,
            endDate: (pageNumber && pageSize) ? formatDate(TODAY,'23:59:59.999Z') : `${endDate.split("T")[0]}T23:59:59.999Z`,
            pageNumber,
            pageSize
        });

        const mappedTransactions =
            transactions &&
            transactions.data &&
            transactions.data
                .filter((transaction) =>
                    checked.includes(transaction.invoiceNumber.toString())
                )
                .map((transaction) => {
                    let datas = {
                        [t("export.invoiceLines.subAccountCode")]:
                            transaction.subAccountCode,
                        [t("export.invoiceLines.invoiceNumber")]:
                            transaction.invoiceNumber,
                        [t("export.invoiceLines.transactionDateTime")]:
                            new Date(transaction.transactionDateTime+'Z'),
                        [t("export.invoiceLines.productCode")]:
                        transaction.productCode,
                        [t("export.invoiceLines.productName")]:
                            transaction.productName.values[
                                transaction.productName.keys.indexOf(
                                    i18n.language
                                )
                                ],
                        [t("export.invoiceLines.quantity")]: Number(
                            Number(transaction.quantity.value).toFixed(3)
                        ),
                        [t("export.invoiceLines.priceAfterExciseTaxes")]:
                        transaction.priceAfterExciseTaxes.value,
                        [t("export.invoiceLines.amountAfterExciseTaxes")]:
                        transaction.amountAfterExciseTaxes.value,
                        [t("export.invoiceLines.gstAmount")]:
                        transaction.gstAmount.value,
                        [t("export.invoiceLines.pstAmount")]:
                        transaction.pstAmount.value,

                        [t("export.invoiceLines.netAmount")]:
                            transaction.netAmount.value,
                        [t("export.invoiceLines.date")]:
                            transaction.transactionDateTime
                                .split("T")[0],
                        [t("export.invoiceLines.time")]:
                            transaction.transactionDateTime.split("T")[1],
                        [t("export.invoiceLines.carrierDeliveryNumber")]:
                        transaction.carrierDeliveryNumber,
                        [t("export.invoiceLines.truckName")]:
                        transaction.truckName,
                        [t("export.invoiceLines.poNumber")]:
                            transaction.purchaseOrderNumber,
                    };
                    return datas;
                });
        Export(mappedTransactions, "harnois_export", [
            t("export.invoiceLines.netAmount"),
        ], fileType);
        setLoading(false);
		fireGTMDownloadFileEvent('facture',fileType+' - Detaille');
    }, [props.startDate, props.endDate, checked]);


    const columns = useMemo(
        () => [
            {
                Header: (
                    <div className="form-check flex flex-row justify-content-end align-items-end">
                        <input
                            type="checkbox"
                            id="select_all"
                            className="form-check-input"
                            value={isSelectAll}
                            onChange={(v) => setIsSelectAll(v.target.checked)}
                        ></input>
                    </div>
                ),
                accessor: "checkBox",
                Cell: (props) => {
                    return (
                        <div className="form-check flex flex-row justify-content-end">
                            <input
                                type="checkbox"
                                value={props.value}
                                className="form-check-input"
                                onChange={(v) => {
                                    handleCheck(
                                        v.target.checked,
                                        v.target.value
                                    );
                                }}
                                checked={
                                    !!checked.includes(props.value.toString())
                                }
                            ></input>
                        </div>
                    );
                },
                disableSortBy: true,
            },
            {
                Header: t("docs.invoices.table.headers.account"),
                accessor: "account",
                Cell: (props) => {
                    return (
                        <Aux>
                            <strong>
                                {props.value.name} - {props.value.code}
                            </strong>
                            <br />
                            <small>
								{props.value.address}
                            </small><br/>
							<a
								onClick={() => onClickInvoiceReviewHandler(props.row.id)}
								className="link link--red link--underline mt-1">
								<u>{t('docs.invoices.table.invoice_review_link')}</u>
							</a>
                        </Aux>
                    );
                },
            },
            {
                Header: t("docs.invoices.table.headers.invoice_no"),
                accessor: "invoiceNumber",
                Cell: (props) => {
                    if (props.value) return props.value;
                    else return "";
                },
            },
            {
                Header: t("docs.invoices.table.headers.amount"),
                accessor: "amount",
                Cell: (props) => {
                    return (
                        <span className="amount-price">
                            {props.value ? <Currency value={props.value.value}  /> : ""}
                        </span>
                    );
                },
            },
            {
                id: "deadlineDate",
                Header: t("docs.invoices.table.headers.due_date"),
                accessor: "dueDate",
                sort: "desc",
                Cell: (props) => {
                    if (props.value) {
                        return (
                            <DateFormated date={props.value} withTime={false} />
                        );
                    } else {
                        return "";
                    }
                },
                sortMethod: (a, b) => {
                    var a1 = new Date(a).getTime();
                    var b1 = new Date(b).getTime();
                    if (a1 < b1) return 1;
                    else if (a1 > b1) return -1;
                    else return 0;
                },
            },
            {
                id: "date",
                Header: t("docs.invoices.table.headers.transaction_date"),
                accessor: "date",
                sort: "desc",
                Cell: (props) => {
                    if (props.value) {
                        return (
                            <DateFormated date={props.value} withTime={false} />
                        );
                    } else {
                        return "";
                    }
                },
                sortMethod: (a, b) => {
                    var a1 = new Date(a).getTime();
                    var b1 = new Date(b).getTime();
                    if (a1 < b1) return 1;
                    else if (a1 > b1) return -1;
                    else return 0;
                },
            },
            {
                Header: t("docs.invoices.table.headers.balance"),
                accessor: "balance",
                Cell: (props) => {
                    if (props.value)
                        return (
                            <span className="amount-price">
                                <Currency value={props.value.value}  />
                            </span>
                        );
                    else return "";
                },
            },
            {
                Header: t("docs.invoices.table.headers.status"),
                accessor: "status",
                Cell: (props) => {
                    if (props.value) return props.value;
                    else return "";
                },
            },
            {
                Header: "PO",
                accessor: "po",
                Cell: (props) => {
                    if (props.value) return props.value;
                    else return "";
                },
            },
            {
                Header: () => null,
                id: "file", // 'id' is required
                accessor: "documentPath",
                Cell: (props) => {
                    if (props.value) {
                        return (
                            <a
                                href={`/rest/v1/communications/download?filename=${encodeURI(
                                    props.value
                                )}`}
								onClick={()=>fireGTMDownloadFileEvent('facture','PDF')}
                                target="_blank"
                                className="link link--red link--upper link--accent link--fs-14"
                            >
                                <i className="far fa-file-download fa-2x"></i>
                            </a>
                        );
                    } else {
                        return "";
                    }
                },
            },
        ],
        [t, i18n, checked, items]
    );

    return (
        <Aux>
            <h2 className="headline headline--section headline--uppercase">
                {t("tabs.invoices")}
            </h2>
            <DateFilters
                startDate={props.startDate}
                endDate={props.endDate}
                setStartDate={props.setStartDate}
                setEndDate={props.setEndDate}
                changeDateFilterType={props.changeDateFilterType}
                dateFilterType={props.dateFilterType}
                enableCurrentPeriod={props.enableCurrentPeriod}
                displayCurrentPeriod={props.displayCurrentPeriod}
                component="invoices"
                labelFilterAll={t("docs.invoices.table.filter.all.invoices")}
            />
            {loading ? (
                <Spinner />
            ) : (
                <Aux>
                    <br />
                    <TableContainer
                        columns={columns}
                        data={items}
                        doXLSExport={doXLSExport}
                        doXLSDetailExport={doXLSDetailExport}
                        checked={checked}
                        initialStateField='date'
                        initialStateDirection='DESC'
                        subAccountCode={subAccountCode}
                    />
					<InvoiceReview showInvoiceReview={invoiceReview} hideInvoiceReview={hideInvoiceReviewHandler} />
				</Aux>
            )}
        </Aux>
    );
};

export default invoices;
